import React from 'react';
import { Plus, Trash, Books, Image as ImageIcon, FileText, ListNumbers, BookOpen } from '@phosphor-icons/react';
import EnhancedRichTextEditor from '../../EnhancedRichTextEditor';
import MediaPickerInput from '../../MediaPickerInput';

export default function BookModule({ metadata, onChange }) {
  const handleChange = (field, value) => {
    onChange({ ...metadata, [field]: value });
  };

  const addChapter = () => {
    const newChapters = [...(metadata.chapters || [])];
    const nextOrder = newChapters.length + 1;
    newChapters.push({ title: '', content: '', order: nextOrder });
    handleChange('chapters', newChapters);
  };

  const updateChapter = (index, field, value) => {
    const newChapters = [...(metadata.chapters || [])];
    newChapters[index] = { ...newChapters[index], [field]: value };
    handleChange('chapters', newChapters);
  };

  const removeChapter = (index) => {
    const newChapters = (metadata.chapters || []).filter((_, i) => i !== index);
    // Reorder remaining chapters
    newChapters.forEach((ch, i) => {
      ch.order = i + 1;
    });
    handleChange('chapters', newChapters);
  };

  return (
    <div className="space-y-6">
      {/* Book Type */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Books size={16} />
          Book Type
        </label>
        <select
          value={metadata.book_type || 'novel'}
          onChange={(e) => handleChange('book_type', e.target.value)}
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        >
          <option value="novel">Novel (उपन्यास)</option>
          <option value="book">Book (पुस्तक)</option>
          <option value="collection">Collection (संग्रह)</option>
        </select>
      </div>

      {/* Cover Image */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <ImageIcon size={16} />
          Cover Image
        </label>
        <MediaPickerInput
          value={metadata.cover_image || ''}
          onChange={(url) => handleChange('cover_image', url)}
          accept="image/*"
          label="Select cover image"
          helpText="Recommended: 1200x1800px (book cover dimensions)"
        />
      </div>

      {/* Summary */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <FileText size={16} />
          Book Summary / Synopsis
        </label>
        <textarea
          value={metadata.summary || ''}
          onChange={(e) => handleChange('summary', e.target.value)}
          placeholder="Brief summary or back-cover description..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={5}
        />
      </div>

      {/* Book Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">ISBN</label>
          <input
            type="text"
            value={metadata.isbn || ''}
            onChange={(e) => handleChange('isbn', e.target.value)}
            placeholder="978-0-123456-78-9"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Publisher</label>
          <input
            type="text"
            value={metadata.publisher || ''}
            onChange={(e) => handleChange('publisher', e.target.value)}
            placeholder="Publisher name"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Publication Year</label>
          <input
            type="number"
            value={metadata.publication_year || ''}
            onChange={(e) => handleChange('publication_year', parseInt(e.target.value) || null)}
            placeholder="2024"
            min="1000"
            max="9999"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Edition</label>
          <input
            type="text"
            value={metadata.edition || ''}
            onChange={(e) => handleChange('edition', e.target.value)}
            placeholder="First Edition"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Total Pages</label>
          <input
            type="number"
            value={metadata.pages || ''}
            onChange={(e) => handleChange('pages', parseInt(e.target.value) || null)}
            placeholder="350"
            min="1"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Genre</label>
          <select
            value={metadata.genre || ''}
            onChange={(e) => handleChange('genre', e.target.value)}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          >
            <option value="">Select genre...</option>
            <option value="Fiction">Fiction</option>
            <option value="Non-Fiction">Non-Fiction</option>
            <option value="Literary Fiction">Literary Fiction</option>
            <option value="Historical Fiction">Historical Fiction</option>
            <option value="Mystery">Mystery</option>
            <option value="Romance">Romance</option>
            <option value="Biography">Biography</option>
            <option value="Memoir">Memoir</option>
          </select>
        </div>
      </div>

      {/* Series Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Series Name (Optional)</label>
          <input
            type="text"
            value={metadata.series || ''}
            onChange={(e) => handleChange('series', e.target.value)}
            placeholder="Part of a series?"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Volume Number</label>
          <input
            type="number"
            value={metadata.volume || ''}
            onChange={(e) => handleChange('volume', parseInt(e.target.value) || null)}
            placeholder="1"
            min="1"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {/* File Upload URLs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">PDF URL (Optional)</label>
          <input
            type="url"
            value={metadata.pdf_url || ''}
            onChange={(e) => handleChange('pdf_url', e.target.value)}
            placeholder="https://example.com/book.pdf"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">EPUB URL (Optional)</label>
          <input
            type="url"
            value={metadata.epub_url || ''}
            onChange={(e) => handleChange('epub_url', e.target.value)}
            placeholder="https://example.com/book.epub"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {/* Chapter Management */}
      <div className="pt-6 border-t border-border">
        <div className="flex items-center justify-between mb-4">
          <label className="text-lg font-semibold flex items-center gap-2">
            <ListNumbers size={20} />
            Chapters
          </label>
          <button
            onClick={addChapter}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
          >
            <Plus size={16} weight="bold" />
            Add Chapter
          </button>
        </div>

        {(metadata.chapters || []).length === 0 && (
          <button
            onClick={addChapter}
            className="w-full p-8 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors"
          >
            <div className="text-center">
              <BookOpen size={32} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Add your first chapter</p>
            </div>
          </button>
        )}

        <div className="space-y-6">
          {(metadata.chapters || []).map((chapter, index) => (
            <div key={index} className="p-6 bg-muted/30 rounded-xl border border-border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <span className="text-muted-foreground">Chapter {chapter.order}</span>
                </h3>
                {(metadata.chapters || []).length > 1 && (
                  <button
                    onClick={() => removeChapter(index)}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
                  >
                    <Trash size={16} />
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <input
                  type="text"
                  value={chapter.title || ''}
                  onChange={(e) => updateChapter(index, 'title', e.target.value)}
                  placeholder="Chapter Title"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors font-semibold"
                />
                <EnhancedRichTextEditor
                  value={chapter.content || ''}
                  onChange={(html) => updateChapter(index, 'content', html)}
                  placeholder="Chapter content..."
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
